import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchConversations,
  fetchConversationMessages,
  moderateConversation,
  clearSelectedConversation,
} from '../../redux/slices/conversationsSlice';
import TableHelper from '../Shared/TableHelper';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import './ConversationsList.css';

const ConversationsList = () => {
  const dispatch = useDispatch();
  const { items, selectedConversation, messages, error } = useSelector(
    (state) => state.conversations
  );
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');

  useEffect(() => {
    dispatch(fetchConversations({ q: search, filter }));
  }, [search, filter, dispatch]);

  const handleSelectConversation = (conversationId) => {
    dispatch(fetchConversationMessages({ conversationId }));
  };

  const handleModerate = (conversationId, currentStatus) => {
    const newStatus = currentStatus === 'open' ? 'closed' : 'open';
    const actionLabel = currentStatus === 'open' ? 'Cerrar' : 'Reabrir';
    
    Swal.fire({
      title: `¿${actionLabel} conversación?`,
      text: currentStatus === 'open'
        ? 'Los usuarios ya no podrán enviarse mensajes en este chat.'
        : 'Los usuarios podrán volver a comunicarse en este chat.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: currentStatus === 'open' ? '#d7192a' : '#28a745',
      cancelButtonColor: '#6c757d',
      confirmButtonText: `Sí, ${actionLabel.toLowerCase()}`,
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(moderateConversation({ conversationId, status: newStatus })).unwrap().then(() => {
          toast.success(`Conversación ${currentStatus === 'open' ? 'cerrada' : 'reabierta'} con éxito`);
        }).catch((err) => {
          toast.error(err || 'Error al moderar la conversación');
        });
      }
    });
  };

  const handleCloseDetail = () => {
    dispatch(clearSelectedConversation());
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Oferta',
        accessor: 'title',
        Cell: ({ value }) => <strong>{value}</strong>,
      },
      {
        Header: 'Participantes',
        accessor: 'candidate_name',
        Cell: ({ row: { original: conv } }) => (
          <div className="participants-cell">
            {conv.candidate_name}
            <small style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '11px' }}>
              con {conv.employer_name}
            </small>
          </div>
        ),
      },
      {
        Header: 'Mensajes',
        accessor: 'message_count',
      },
      {
        Header: 'Última actividad',
        accessor: 'last_message',
        Cell: ({ value }) => value ? new Date(value).toLocaleDateString('es-ES') : 'Sin mensajes',
      },
      {
        Header: 'Estado',
        accessor: 'status',
        Cell: ({ value }) => (
          <span className={`badge ${value}`}>
            {value === 'open' ? 'Abierta' : 'Cerrada'}
          </span>
        ),
      },
      {
        Header: 'Acciones',
        id: 'actions',
        Cell: ({ row: { original: conv } }) => (
          <div className="actions">
            <button
              onClick={() => handleSelectConversation(conv.id)}
              className="btn btn-sm btn-link"
            >
              Revisar
            </button>
            <button
              onClick={() => handleModerate(conv.id, conv.status)}
              className={`btn btn-sm ${conv.status === 'open' ? 'btn-danger' : 'btn-success'}`}
            >
              {conv.status === 'open' ? 'Cerrar' : 'Reabrir'}
            </button>
          </div>
        ),
      },
    ],
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <div className="conversations-container">
      <div className="conversations-header">
        <h1>Mensajería segura</h1>
        <p>Revisa actividad y modera conversaciones.</p>
      </div>

      <div className="conversations-toolbar">
        <input
          type="text"
          placeholder="Filtrar por oferta, profesional o empleador..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="filter-select">
          <option value="">Todas</option>
          <option value="open">Abiertas</option>
          <option value="closed">Cerradas</option>
        </select>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className={`conversations-layout ${selectedConversation ? 'has-detail' : ''}`}>
        <div className="conversations-table-container">
          <TableHelper columns={columns} data={items} exportFileName="conversaciones" />
        </div>

        {selectedConversation && (
          <aside className="messages-panel">
            <button onClick={handleCloseDetail} className="btn-close">×</button>
            <div className="messages-header">
              <span className="badge-small">CONVERSACIÓN #{selectedConversation.id}</span>
              <h3>{selectedConversation.title}</h3>
              <p>
                {selectedConversation.candidate_name} con{' '}
                {selectedConversation.employer_name}
              </p>
            </div>

            <div className="messages-list">
              {messages.length > 0 ? (
                messages.map((msg, idx) => (
                  <div key={idx} className="message">
                    <strong>{msg.sender_name}</strong>
                    <p>{msg.body}</p>
                    <small>{new Date(msg.created_at).toLocaleString('es-ES')}</small>
                  </div>
                ))
              ) : (
                <div className="no-messages">No hay mensajes.</div>
              )}
            </div>
          </aside>
        )}
      </div>
    </div>
  );
};

export default ConversationsList;
